<template>
  <section class="md:my-24 mt-3 grid gap-y-5">
    <ImportModal
      v-if="showImportModal"
      :showModal="showImportModal"
      @onCloseImportModal="showImportModal = false"
      @onUpdateTable="usersPaginated"
    />
    <div id="users-header" class="flex items-center justify-between">
      <h4>{{ $t("labels.generic", { pt: "Usuários", en: "Users" }) }}</h4>
      <div class="md:flex items-center gap-3 relative">
        <Button
          id="actions-btn"
          :label="`${$t('labels.generic', { pt: 'Ações', en: 'Actions' })}`"
          class="btn-primary p-button-sm"
          icon="pi pi-plus"
          @click="openActionOptions()"
        />
        <div
          ref="ActionOptions"
          class="bg-white w-72 py-2 shadow-md rounded-md absolute top-12 right-0 z-50"
          v-if="showActionOptions"
        >
          <Button
            id="add-user-btn"
            class="btn-simple p-button-sm w-full gap-2"
            @click="go.redirectTo('formUser')"
          >
            <v-icon name="md-personaddalt-outlined"></v-icon>
            <p class="text-[14px]">
              {{
                $t("labels.generic", {
                  pt: "Cadastrar usuário",
                  en: "Add user",
                })
              }}
            </p>
          </Button>
          <Button
            v-if="user.role === 'ADMINISTRATOR'"
            id="add-user-btn"
            class="btn-simple p-button-sm w-full gap-2"
            @click="openImportModal()"
          >
            <v-icon name="md-uploadfile-round"></v-icon>
            <p class="text-[14px]">
              {{
                $t("labels.generic", {
                  pt: "Importar usuários",
                  en: "Users imports",
                })
              }}
            </p>
          </Button>

          <Button
            id="download-template-btn"
            class="btn-simple"
            @click="downloadCSVTemplate"
          >
            <v-icon name="md-insertdrivefile-round"></v-icon>
            <p class="text-[14px] ml-1">
              {{
                $t("labels.generic", {
                  pt: "Baixar modelo de importação",
                  en: "Download import model",
                })
              }}
            </p>
          </Button>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg border border-gray-300">
      <Table
        :slotData="users"
        :columns="columns"
        :actions="actions"
        :loading="loading"
        :params="params"
        :filters="filters"
        :hasStatus="true"
        @onEdit="(e) => goToEdit(e)"
        @onChangeStatus="(e) => changeUserStatus(e)"
        @onPaginate="paginate($event)"
        @onToggleFilter="(e, type) => toggleFilter(e, type)"
        @onFilter="filterFor()"
        @onClearFilter="clearFilter()"
      >
        <template #filters>
          <InputText v-if="filterType == 'name'" v-model="filters.name" />
          <InputText v-if="filterType == 'login'" v-model="filters.email" />
          <InputText
            v-if="filterType == 'organization'"
            v-model="filters.organization"
          />
          <Dropdown
            v-if="filterType == 'role'"
            v-model="filters.role.value"
            :options="filters.role.options"
            optionLabel="name"
            optionValue="code"
            placeholder="Selecione o tipo"
          />
          <Dropdown
            v-if="filterType == 'status'"
            v-model="filters.status.value"
            :options="filters.status.options"
            optionLabel="name"
            optionValue="code"
            placeholder="Selecione o status"
          />
        </template>
      </Table>
    </div>
  </section>
</template>

<script lang="ts">
import { QueryParams } from "@/models/query-params.model";
import { CsvFile, UserForm, Users } from "@/models/user.model";
import { UsersRest } from "@/services/rest/users.rest";
import { GoToRoute } from "@/utils/go-to.util";
import HandleError from "@/utils/handle-errors.util";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { Parser } from "@json2csv/plainjs";
import { saveAs } from "file-saver";
import { delay } from "rxjs";
import { defineAsyncComponent, defineComponent } from "vue";
import { UsersService } from "./users.service";
import {
  roleDropdown,
  statusDropdown,
  userActions,
  userColumns,
  users_template,
} from "./users.util";

const ImportModal = defineAsyncComponent(
  () => import("./ImportModal/import-modal.vue")
);

export default defineComponent({
  name: "users",
  components: { ImportModal },
  computed: {
    service(): UsersService {
      return new UsersService();
    },
    rest(): UsersRest {
      return new UsersRest();
    },
    errors(): HandleError {
      return new HandleError();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    user(): any {
      return getUserFromStorage();
    },
  },
  data() {
    return {
      filters: {
        isCleareable: false,
        name: null,
        organization: null,
        email: null,
        role: roleDropdown,
        status: statusDropdown,
      } as any,
      selectedUsers: null as any,
      columns: userColumns,
      actions: userActions as any,
      params: new QueryParams(),
      filterType: "" as string,
      displayModal: false as boolean,
      loading: true as boolean,
      users: [] as Users[],
      importData: new CsvFile(),
      showActionOptions: false as boolean,
      showImportModal: false as boolean,
    };
  },
  created() {
    this.actions[0].event = (e) => this.goToEdit(e);
  },
  mounted() {
    this.usersPaginated();
  },
  methods: {
    goToEdit(user: any): void {
      this.go.redirectToPage("formUser", {
        id: user.id,
        role: user.role?.value?.toLowerCase(),
      });
    },
    paginate(event: { page: number; rows: number }): void {
      const params = new QueryParams(event.page, event.rows);
      params.name = this.filters.name;
      params.role = this.filters.role?.value;
      params.organization = this.filters.organization;
      params.status = this.filters.status?.value;
      this.setPage(params);
    },
    setPage(queryParams: QueryParams): void {
      this.loading = true;

      this.service.users.pipe(delay(500)).subscribe({
        next: (response) => {
          this.loading = false;
          this.users = response.content;
          this.params.total = response.total;
        },
      });
      this.service.getUsers(queryParams);
    },
    usersPaginated(): void {
      const page = this.params.page;
      const size = this.params.size;
      const params = new QueryParams(page, size);
      this.setPage(params);
    },
    onUpload(event: any): void {
      this.displayModal = true;
      this.importData.fileError = false;
      var { files } = event.target;
      if (files[0]?.type == "text/csv") {
        const formData = new FormData();
        formData.append("file", files[0]);
        this.importData = new CsvFile();
        this.importData.fileError = false;
        this.importData.file.name = files[0]?.name;
        this.rest
          .importUsers(formData)
          .pipe(delay(3000))
          .subscribe({
            next: (response) => {
              this.usersPaginated();
              if (response) {
                const csv: any = this.service.csvToArray(response);
                this.importData.csv = response;
                csv?.splice(1, 1);
                this.importData.errors = csv;
              } else {
                this.displayModal = false;
              }
            },
            error: () => {
              this.importData = new CsvFile();
            },
          });
      } else {
        this.importData = new CsvFile();
      }
      document.removeEventListener("click", this.closeActionOptions);
    },
    downloadCSV(): void {
      const blob = new Blob([this.importData?.csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      saveAs(url, `relatório_de _erros.csv`);
    },
    onClose(): void {
      this.displayModal = false;
      setTimeout(() => {
        document.addEventListener("click", this.closeActionOptions);
      }, 100);
    },
    toggleFilter(event: any, type: string): void {
      this.filterType = type;
    },
    filterFor(): void {
      this.filters.isCleareable = this.isCleareable();
      var params = new QueryParams();
      params.name = this.filters.name;
      params.role = this.filters.role?.value;
      params.organization = this.filters.organization;
      params.status = this.filters.status?.value;
      params.email = this.filters.email;
      this.setPage(params);
    },
    clearFilter(): void {
      this.filters.isCleareable = false;
      this.filters.name = null;
      this.filters.role.value = null;
      this.filters.organization = null;
      this.filters.status.value = null;
      this.filters.email = null;
      this.usersPaginated();
    },
    isCleareable(): boolean {
      return (
        this.filters.name !== "" ||
        this.filters.email !== "" ||
        this.filters.role.value !== null ||
        this.filters.organization !== "" ||
        this.filters.status.value !== null
      );
    },
    changeUserStatus(user: any): void {
      this.loading = true;
      const data = {
        cpf: user.cpf,
        login: user.login,
        name: user.name,
        role: user.role.value,
        organization: user.organization?.id,
        status: this.service.statusChange(user.status?.value),
        birthDate: user.birthDate,
      } as UserForm;

      this.rest.putUser(user?.id, data).subscribe({
        next: (response) => {
          this.loading = false;
          var index = this.users.findIndex((e: any) => e.id == response.id);
          response.status = this.service.remakeObjectStatus(response.status);
          response.role = this.service.remakeObjectRole(response.role);
          this.users[index] = response;
          this.$toast.add(ToastFactory.success(Messages.USER_STATUS));
        },
      });
    },
    inputReset(event: any): void {
      event.target.value = "";
    },
    downloadCSVTemplate(): void {
      const csv = new Parser({ delimiter: ";" }).parse(users_template);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      saveAs(url, `template_student.csv`);
      this.$toast.add(ToastFactory.warn(Messages.CSV_OBS));
    },
    openActionOptions(): void {
      if (!this.showActionOptions) {
        this.showActionOptions = true;
        setTimeout(() => {
          document.addEventListener("click", this.closeActionOptions);
        }, 100);
      }
    },
    closeActionOptions(event): void {
      const myDiv = this.$refs.ActionOptions as HTMLElement;
      if (myDiv && !myDiv.contains(event.target as Node)) {
        this.showActionOptions = false;
        document.removeEventListener("click", this.closeActionOptions);
      }
    },
    openImportModal(): void {
      this.showActionOptions = false;
      document.removeEventListener("click", this.closeActionOptions);
      this.showImportModal = true;
    },
  },
});
</script>
